/**
 * Representa un ítem del carrusel del hero del home.
 *
 * Hay dos clases de ítems:
 *  - **Dinámicos**: provienen de la Biblioteca de Media (`MediaItem`), se almacenan en
 *    `carouselConfig/main.items` (Firestore) y son configurables por un editor con
 *    permiso `ACCESS_CONTENT_EDITOR`.
 *  - **Fijo (institucional)**: la constante {@link STATIC_CAROUSEL_ITEM}. Representa el
 *    contenido visual estático actual del hero, no se persiste en Firestore y se
 *    concatena en runtime al final del array por {@link selectCarouselItemsWithFixed}.
 *    Se identifica por `fixed === true`.
 */
export interface CarouselItem {
  /** ID del `MediaItem` en Firestore. Cadena vacía para el ítem fijo. */
  mediaId: string
  /** Nombre legible del archivo (visible en el modal de edición). */
  name: string
  /** URL pública del archivo. Cadena vacía para el ítem fijo (render JSX estático). */
  url: string
  /** Clasificación del recurso: `video` o `image`. */
  type: 'video' | 'image'
  /** Posición 0-indexada en el carrusel. Determinada por el índice en el array. */
  order: number
  /**
   * Marca el ítem como institucional fijo. Solo presente en
   * {@link STATIC_CAROUSEL_ITEM}; **nunca se almacena en Firestore**.
   * Tipo literal `true` (no `boolean`) por diseño — su mera presencia identifica al fijo.
   */
  fixed?: true
  /**
   * Modo de ajuste visual del recurso al contenedor del hero:
   *  - `'cover'` (defecto histórico): llena toda el área recortando lo que sobre.
   *    Garantiza que no queden franjas vacías; sacrifica partes del contenido.
   *  - `'contain'`: muestra el recurso completo respetando su relación, dejando
   *    franjas vacías (con fondo negro) en los lados o arriba/abajo si es necesario.
   * Opcional para retrocompatibilidad — ítems guardados antes de este campo se
   * interpretan como `'cover'`.
   */
  fitMode?: 'cover' | 'contain'
}

/**
 * Documento único en `carouselConfig/main` que persiste la configuración del carrusel.
 * Solo contiene los ítems dinámicos; el ítem fijo se agrega en runtime y nunca se
 * incluye en este payload.
 */
export interface CarouselConfig {
  /** Identificador fijo del documento; siempre `'main'`. */
  id: 'main'
  /** Ítems dinámicos en orden. No incluye {@link STATIC_CAROUSEL_ITEM}. */
  items: CarouselItem[]
  /** Fecha del último guardado en ISO 8601. */
  updatedAt: string
  /** UID del editor que realizó el último guardado. */
  updatedBy: string
}

/** Máximo de ítems dinámicos configurables en el carrusel (no cuenta el fijo). */
export const MAX_CAROUSEL_ITEMS = 5

/**
 * Contenido visual estático actual del hero — siempre al final, nunca eliminable,
 * jamás almacenado en Firestore. Se agrega en runtime por
 * `selectCarouselItemsWithFixed`. El `HeroCarousel` lo identifica por `fixed === true`
 * y renderiza el JSX estático del hero (delegado en `HeroStaticContent`).
 */
export const STATIC_CAROUSEL_ITEM: CarouselItem = {
  mediaId: '',
  name: 'Contenido institucional',
  url: '',
  type: 'image',
  order: -1,
  fixed: true,
}
